import { Button, Dropdown, Grid, MenuProps } from "antd";
import { ItemType } from "antd/es/menu/interface";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Globe from "../../assets/images/icons/Globe";
import { useLocalStorage } from "../../context/useLocalStorage";
import localStorageKey from "../../enumerations/localStorageKey";
import { pushGtag } from "../../plugins/utils";
import CustomDrawer from "../CustomDrawer";
import { P1 } from "../NormalFonts";
import { LanguageDropdownProps, LanguageResponse } from "./data";
import { useGetLanguage } from "./language.hooks";
import styles from "./styles.module.scss";

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({
  showDrawer,
  setShowDrawer,
}) => {
  const { t } = useTranslation();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const [defaultLanguage, setDefaultLanguage] = useLocalStorage(
    localStorageKey.DEFAULT_LANG,
    "zh-TW"
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { data: languages } = useGetLanguage({
    enabled: isOpen === true || showDrawer === true,
  });
  const [languageDropdown, setLanguageDropdown] = useState<LanguageResponse[]>(
    []
  );

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const onClick: MenuProps["onClick"] = ({ key }) => {
    setLanguage(key);
  };

  const setLanguage = (code: string) => {
    pushGtag({
      event: "change_language",
      code,
    });

    setDefaultLanguage(code);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  useEffect(() => {
    if (!languages) return;
    setLanguageDropdown(
      languages?.map((lang: LanguageResponse) => {
        return {
          ...lang,
          key: lang.code,
          label: (
            <div
              key={lang.code}
              className={`${styles.menuitem} ${
                defaultLanguage === lang.code ? styles.active : ""
              }`}
            >
              <P1 color="var(--Neutral-dark-70)">
                {t(`language.${lang.code}`)}
              </P1>
            </div>
          ),
          type: "item", // Ensure the type property is added
        };
      })
    );
  }, [languages]);

  const onClose = () => {
    setShowDrawer(false);
  };

  const renderLanguageList = () => {
    if (!languages) return null;
    return languages.map((lang: LanguageResponse, index: number) => {
      return (
        <P1
          key={index}
          className={styles.link}
          onClick={() => {
            setLanguage(lang.code);
          }}
        >
          {t(`language.${lang.code}`)}
        </P1>
      );
    });
  };

  return (
    <div>
      {screens.md ? (
        <Dropdown
          menu={{ items: languageDropdown as ItemType[], onClick }}
          trigger={["click"]}
          placement="bottomRight"
          onOpenChange={handleOpenChange}
        >
          <Button
            className={styles.globeIcon}
            size={"middle"}
            type="primary"
            icon={
              <Globe
                style={
                  screens.md
                    ? { fontSize: 24, color: "var(--Neutral-light-10)" }
                    : {
                        fontSize: 20,
                        color: "var(--primary-90)",
                      }
                }
              />
            }
          />
        </Dropdown>
      ) : (
        <Button
          className={styles.globeIcon}
          shape={"circle"}
          size={"small"}
          onClick={() => {
            setShowDrawer(true);
          }}
          style={{
            background: "var(--bg)",
            filter: "drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.20))",
          }}
          type="primary"
          icon={
            <Globe
              style={
                screens.md
                  ? { fontSize: 24, color: "var(--Neutral-light-10)" }
                  : {
                      fontSize: 20,
                      color: "var(--primary-90)",
                    }
              }
            />
          }
        />
      )}
      <CustomDrawer
        placement={"left"}
        closable={true}
        onClose={onClose}
        open={showDrawer}
        size={"large"}
        width={"100vw"}
      >
        <div className={styles.drawerBodyContainer}>{renderLanguageList()}</div>
      </CustomDrawer>
    </div>
  );
};

export default LanguageDropdown;
